import React, { useMemo, useState } from 'react';

import ArticleDetailsModal from './ArticleDetailsModal';
// import { formatPrice } from '../../../../utils/priceUtils';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down.svg';
import Switch from '../../../../composants/Switch/Switch';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Article {
  id: number;
  code: string;
  nom: string;
  description?: string;
  seuil_a?: number;
  seuil_b?: number;
  unite: string;
}

export interface PrixLigne {
  prix_a: string;
  prix_b: string;
  prix_c: string;
}

interface SelectedLot {
  id: number;
  departement?: { id: number; nom: string };
}

export interface RegionGroup {
  regionId: number;
  regionNom: string;
  lots: SelectedLot[];
}

// ── PrixInput — séparateur de milliers fr-FR ─────────────────────────────────

function PrixInput({
  value,
  disabled,
  className,
  title,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  className?: string;
  title?: string;
  onChange: (raw: string) => void;
}) {
  const [focused, setFocused] = useState(false);

  const displayValue = focused
    ? value
    : value !== '' && !isNaN(parseFloat(value.replace(/\s/g, '').replace(',', '.')))
      ? parseFloat(value.replace(/\s/g, '').replace(',', '.')).toLocaleString('fr-FR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        })
      : value;

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      title={title}
      value={displayValue}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        // Ne garder que chiffres, point, virgule, signe minus
        const raw = e.target.value.replace(/\s/g, '').replace(',', '.');
        onChange(raw);
      }}
    />
  );
}

// ── Table articles ────────────────────────────────────────────────────────────

function ArticlesTable({
  articles,
  prix,
  disabled,
  errorArticleIds,
  degressifForAll,
  onChangePrix,
  onVoirDetails,
}: {
  articles: Article[];
  prix: Record<number, PrixLigne>;
  disabled: boolean;
  errorArticleIds?: Set<number>;
  degressifForAll?: boolean;
  onChangePrix: (articleId: number, champ: keyof PrixLigne, val: string) => void;
  onVoirDetails: (article: Article) => void;
}) {
  return (
    <div className="articles-table-wrapper">
      <table className="articles-table">
        <thead>
          <tr>
            <th>Réf</th>
            <th>Article</th>
            <th>Description</th>
            <th>Seuil A</th>
            <th>Seuil B</th>
            <th>Unité</th>
            <th>Prix par Unité pour des unités inférieures au seuil A</th>
            <th>Prix par Unité pour des unités comprises entre les seuils A et B</th>
            <th>Prix par Unité pour des unités supérieures au seuil B</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((article, i) => {
            const ligne = prix[article.id];
            const isIncomplete = !ligne?.prix_a || !ligne?.prix_b || !ligne?.prix_c;
            const isForfait = article.unite?.toLowerCase() === 'forfait';

            // Validation dégressivité : A >= B >= C
            // En mode dégressifForAll, s'applique à tous les articles (pas seulement forfait)
            const pA = parseFloat(ligne?.prix_a ?? '');
            const pB = parseFloat(ligne?.prix_b ?? '');
            const pC = parseFloat(ligne?.prix_c ?? '');
            const applyDegressif = isForfait || !!degressifForAll;
            const hasValues = applyDegressif && !isNaN(pA) && !isNaN(pB) && !isNaN(pC) && (pA > 0 || pB > 0 || pC > 0);
            const isNotDecreasing = hasValues && (pA < pB || pB < pC);
            const warningTitle = isNotDecreasing ? 'Les prix doivent être égaux ou dégressifs (Prix A ≥ Prix B ≥ Prix C)' : undefined;

            return (
              <tr key={article.id} className={[
                isIncomplete ? 'article-row--incomplete' : '',
                isNotDecreasing ? 'article-row--error' : '',
                errorArticleIds?.has(article.id) ? 'article-row--error' : '',
              ].filter(Boolean).join(' ')}>
                <td>{article.code}</td>
                <td>{article.nom}</td>
                <td>
                  <button
                    type="button"
                    className="btn-voir-details"
                    onClick={() => onVoirDetails(article)}
                  >
                    Voir les détails
                  </button>
                </td>
                <td>{article.seuil_a ?? '—'}</td>
                <td>{article.seuil_b ?? '—'}</td>
                <td>{article.unite}</td>
                <td>
                  <div className="prix-input-cell">
                    <PrixInput
                      className={`prix-input${isNotDecreasing ? ' prix-input--error' : ''}`}
                      value={ligne?.prix_a ?? ''}
                      disabled={disabled}
                      title={warningTitle}
                      onChange={(raw) => {
                        onChangePrix(article.id, 'prix_a', raw);
                        if (isForfait) {
                          onChangePrix(article.id, 'prix_b', raw);
                          onChangePrix(article.id, 'prix_c', raw);
                        }
                      }}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
                <td>
                  <div className="prix-input-cell">
                    <PrixInput
                      className={`prix-input${isNotDecreasing ? ' prix-input--error' : ''}`}
                      value={ligne?.prix_b ?? ''}
                      disabled={disabled || isForfait}
                      title={isForfait ? 'Prix identique au Prix A pour les forfaits' : warningTitle}
                      onChange={(raw) => onChangePrix(article.id, 'prix_b', raw)}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
                <td>
                  <div className="prix-input-cell">
                    <PrixInput
                      className={`prix-input${isNotDecreasing ? ' prix-input--error' : ''}`}
                      value={ligne?.prix_c ?? ''}
                      disabled={disabled || isForfait}
                      title={isForfait ? 'Prix identique au Prix A pour les forfaits' : warningTitle}
                      onChange={(raw) => onChangePrix(article.id, 'prix_c', raw)}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Accordéon (avec badge de complétion — Story 13.6) ──────────────────────

function Accordion({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="accordion">
      <button className="accordion-header" onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        {badge && <span className="accordion-badge">{badge}</span>}
        <ArrowDownIcon className={`accordion-chevron${open ? ' accordion-chevron--open' : ''}`} />
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SectionOffreFinanciereProps {
  poidsOffreFinanciere: number;
  articles: Article[];
  canEdit: boolean;
  isRegional: boolean;
  regionGroups: RegionGroup[];
  prixIdentiques: boolean;
  prixLignes: Record<number, PrixLigne>;
  prixParLot: Record<number, Record<number, PrixLigne>>;
  prixParRegion: Record<number, Record<number, PrixLigne>>;
  selectedLots: SelectedLot[];
  /** Erreurs de validation API : clés "prix.{article_id}" → messages */
  apiErrors?: Record<string, string[]> | null;
  /** Si true, la validation dégressivité s'applique à tous les articles (pas seulement forfait) */
  degressifForAll?: boolean;
  onTogglePrixIdentiques: () => void;
  onChangePrix: (articleId: number, champ: keyof PrixLigne, val: string) => void;
  onChangePrixLot: (lotId: number, articleId: number, champ: keyof PrixLigne, val: string) => void;
  onChangePrixRegion: (regionId: number, articleId: number, champ: keyof PrixLigne, val: string) => void;
}

// ── Section "Offre financière" ──────────────────────────────────────────────

const SectionOffreFinanciere: React.FC<SectionOffreFinanciereProps> = ({
  poidsOffreFinanciere,
  articles,
  canEdit,
  isRegional,
  regionGroups,
  prixIdentiques,
  prixLignes,
  prixParLot,
  prixParRegion,
  selectedLots,
  apiErrors,
  degressifForAll,
  onTogglePrixIdentiques,
  onChangePrix,
  onChangePrixLot,
  onChangePrixRegion,
}) => {
  // Extraire les article IDs en erreur depuis les clés "prix.{article_id}"
  const errorArticleIds = useMemo<Set<number>>(() => {
    if (!apiErrors) return new Set();
    const ids = new Set<number>();
    Object.keys(apiErrors).forEach((key) => {
      const match = key.match(/^prix\.([0-9]+)$/);
      if (match) ids.add(parseInt(match[1], 10));
    });
    return ids;
  }, [apiErrors]);
  // Label du toggle adapté au type de lot
  const toggleLabel = isRegional
    ? 'Mes prix seront les mêmes pour chaque lot géographique'
    : 'Mes prix seront les mêmes pour chaque lot géographique';

  // Calcul du total HT et de la commission 5%
  // const { totalHT, commission } = useMemo(() => {
  //   let total = 0;
  //   const sumPrix = (p: PrixLigne) => {
  //     total += parseFloat(p.prix_a) || 0;
  //     total += parseFloat(p.prix_b) || 0;
  //     total += parseFloat(p.prix_c) || 0;
  //   };

  //   if (prixIdentiques) {
  //     Object.values(prixLignes).forEach(sumPrix);
  //   } else if (isRegional) {
  //     Object.values(prixParRegion).forEach((regionPrix) =>
  //       Object.values(regionPrix).forEach(sumPrix)
  //     );
  //   } else {
  //     Object.values(prixParLot).forEach((lotPrix) =>
  //       Object.values(lotPrix).forEach(sumPrix)
  //     );
  //   }

  //   return { totalHT: total, commission: total * 0.05 };
  // }, [prixIdentiques, prixLignes, prixParRegion, prixParLot, isRegional]);

  // ── Détection prix manquants (Story 13.6) ──────────────────────────────────

  const completionInfo = useMemo(() => {
    const isComplete = (p: PrixLigne | undefined) =>
      p != null && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';

    const totalArticles = articles.length;

    if (prixIdentiques) {
      const nbComplets = articles.filter((a) => isComplete(prixLignes[a.id])).length;
      return { total: totalArticles, complets: nbComplets, manquants: totalArticles - nbComplets, parGroupe: null };
    }

    if (isRegional) {
      const parGroupe = regionGroups.map(({ regionId, regionNom }) => {
        const regPrix = prixParRegion[regionId] ?? {};
        const nbComplets = articles.filter((a) => isComplete(regPrix[a.id])).length;
        return { id: regionId, nom: regionNom, total: totalArticles, complets: nbComplets };
      });
      const totalAll = parGroupe.reduce((s, g) => s + g.total, 0);
      const completsAll = parGroupe.reduce((s, g) => s + g.complets, 0);
      return { total: totalAll, complets: completsAll, manquants: totalAll - completsAll, parGroupe };
    }

    // Lot 2xx
    const parGroupe = selectedLots.map((lot) => {
      const lotPrix = prixParLot[lot.id] ?? {};
      const nbComplets = articles.filter((a) => isComplete(lotPrix[a.id])).length;
      return { id: lot.id, nom: lot.departement?.nom ?? `Lot ${lot.id}`, total: totalArticles, complets: nbComplets };
    });
    const totalAll = parGroupe.reduce((s, g) => s + g.total, 0);
    const completsAll = parGroupe.reduce((s, g) => s + g.complets, 0);
    return { total: totalAll, complets: completsAll, manquants: totalAll - completsAll, parGroupe };
  }, [articles, prixIdentiques, prixLignes, isRegional, regionGroups, prixParRegion, selectedLots, prixParLot]);

  // Badge de complétion pour un accordéon
  const getCompletionBadge = (groupId: number): string | undefined => {
    const groupInfo = completionInfo.parGroupe?.find((g) => g.id === groupId);
    if (!groupInfo) return undefined;
    if (groupInfo.complets === groupInfo.total) return `${groupInfo.complets}/${groupInfo.total} ✅`;
    if (groupInfo.complets === 0) return `0/${groupInfo.total} ❌`;
    return `${groupInfo.complets}/${groupInfo.total} ⚠️`;
  };

  // État modal détails article
  const [detailArticle, setDetailArticle] = useState<Article | null>(null);

  const handleVoirDetails = (article: Article) => setDetailArticle(article);
  const handleCloseDetails = () => setDetailArticle(null);

  // Rendu du contenu "prix différents" selon le type de lot
  const renderPrixDifferents = () => {
    if (isRegional) {
      if (regionGroups.length === 0) {
        return (
          <p className="section-empty">
            Sélectionnez d'abord des régions dans la section "Choix des lots".
          </p>
        );
      }
      return (
        <div className="accordeons-list">
          {regionGroups.map(({ regionId, regionNom }) => (
            <Accordion key={regionId} title={regionNom} badge={getCompletionBadge(regionId)}>
              <ArticlesTable
                articles={articles}
                prix={prixParRegion[regionId] ?? {}}
                disabled={!canEdit}
                errorArticleIds={errorArticleIds}
                degressifForAll={degressifForAll}
                onChangePrix={(articleId, champ, val) =>
                  onChangePrixRegion(regionId, articleId, champ, val)
                }
                onVoirDetails={handleVoirDetails}
              />
            </Accordion>
          ))}
        </div>
      );
    }

    if (selectedLots.length === 0) {
      return (
        <p className="section-empty">
          Sélectionnez d'abord les lots géographiques dans la section "Choix des lots".
        </p>
      );
    }
    return (
      <div className="accordeons-list">
        {selectedLots.map((lot) => (
          <Accordion key={lot.id} title={lot.departement?.nom ?? ''} badge={getCompletionBadge(lot.id)}>
            <ArticlesTable
              articles={articles}
              prix={prixParLot[lot.id] ?? {}}
              disabled={!canEdit}
              errorArticleIds={errorArticleIds}
              degressifForAll={degressifForAll}
              onChangePrix={(articleId, champ, val) =>
                onChangePrixLot(lot.id, articleId, champ, val)
              }
              onVoirDetails={handleVoirDetails}
            />
          </Accordion>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className='section-content'>
        {/* Bloc explicatif seuils et dégressivité */}
        {articles.length > 0 && (
          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            fontSize: '1.1rem',
            color: '#374151',
            lineHeight: '1.6',
          }}>
            <p style={{ margin: 0, whiteSpace: 'pre-line' }}>
              Il est précisé que pour chaque article, il est indiqué 2 seuils de quantité (A et B). Ainsi, il est donné la possibilité au prestataire de faire varier son prix en fonction de la quantité qui sera commandée par l'acheteur. A cet effet, il indique 3 prix ; le premier est le « Prix par Unité pour des unités inférieures au seuil A » ; le deuxième est le « Prix par Unité pour des unités comprises entre les seuils A et B » ; et le troisième est le « Prix par Unité pour des unités supérieures au seuil B ». Les unités principales utilisées sont : « ml » pour mètre linéaire ; « F » pour forfait ; « U » pour unités ; « J » pour jours ; « m2 » pour mètre-carré ; « m3 » pour mètre cube ; « T » pour Tonnes ; « PDL » pour Points de Livraison ; « KWh » pour Kilo Watt heure ; « MWh cumac » pour Méga Watt heure cumac ; « S » pour semaine ; « € (budget) » pour budget de l'opération -&gt; dans ce dernier cas, il convient de marquer votre offre en % sous la forme 0.XX€ (exemple : si vous souhaitez pratiquer 5% d'honoraire sur le budget de l'opération, il convient d'indiquer 0.05 € comme prix unitaire pour l'unité « € »).
            </p>
          </div>
        )}

        {/* Toggle prix identiques */}
        <div className="prix-toggle-wrapper">
          <div className="prix-toggle-label">
            <span>{toggleLabel}</span>
            <Switch
              id="prix-identiques-switch"
              isOn={prixIdentiques}
              handleToggle={() => onTogglePrixIdentiques()}
              onColor="#f97316"
              disabled={!canEdit}
            />
          </div>
        </div>

        {/* Compteur de complétion des prix (Story 13.6) */}
        {/* {articles.length > 0 && (
          <div className="prix-completion-counter">
            <span>📊 Articles renseignés : {completionInfo.complets} / {completionInfo.total}</span>
            {completionInfo.manquants > 0 && (
              <>
                <span className="prix-manquants"> — {completionInfo.manquants} prix manquant(s)</span>
                <button
                  type="button"
                  className="btn-scroll-manquant"
                  onClick={() => {
                    const el = document.querySelector('.article-row--incomplete');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  Voir le premier incomplet ↓
                </button>
              </>
            )}
          </div>
        )} */}

        {articles.length === 0 ? (
          <p className="section-empty">Aucun article défini pour ce lot technique.</p>
        ) : prixIdentiques ? (
          <ArticlesTable
            articles={articles}
            prix={prixLignes}
            disabled={!canEdit}
            errorArticleIds={errorArticleIds}
            degressifForAll={degressifForAll}
            onChangePrix={onChangePrix}
            onVoirDetails={handleVoirDetails}
          />
        ) : (
          renderPrixDifferents()
        )}

        {/* Modal détails article */}
        {detailArticle && (
          <ArticleDetailsModal
            article={detailArticle}
            onClose={handleCloseDetails}
          />
        )}
      </div>
    </>
  );
};

export default SectionOffreFinanciere;
